import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, firestore } from '../views/firebaseConfig';
import { doc, getDoc, setDoc, deleteField, GeoPoint, serverTimestamp } from 'firebase/firestore';

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 10000, maximumAge: 45000 };

export default function useUserLocation() {
  const [profileChecked, setProfileChecked] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [userCenter, setUserCenter] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [permissionState, setPermissionState] = useState('prompt');
  const [locationError, setLocationError] = useState('');
  const watchRef = useRef(null);

  const savePresence = useCallback(async (loc) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    await setDoc(doc(firestore, 'userprofiles', uid), {
      onlineStatus: 'online',
      location: new GeoPoint(loc.lat, loc.lng),
      locationUpdatedAt: serverTimestamp(),
    }, { merge: true });
  }, []);

  const acceptPosition = useCallback(({ coords }) => {
    const loc = { lat: coords.latitude, lng: coords.longitude };
    setUserCenter(loc);
    setAccuracy(Number.isFinite(coords.accuracy) ? coords.accuracy : null);
    setPermissionState('granted');
    setLocationError('');
    savePresence(loc).catch(() => {});
  }, [savePresence]);

  const handleGeoError = useCallback((error) => {
    const denied = error?.code === 1;
    setPermissionState(denied ? 'denied' : 'prompt');
    setLocationError(denied
      ? 'Location access is blocked. Enable it in your browser site settings to center the map on you.'
      : 'We could not get your location. Check GPS/location services and try again.');
  }, []);

  const requestLocation = useCallback(() => {
    setShowLocationPrompt(false);
    setLocationError('');
    if (!navigator.geolocation) {
      setPermissionState('unsupported');
      setLocationError('This browser does not support location services.');
      return;
    }
    navigator.geolocation.getCurrentPosition(acceptPosition, handleGeoError, GEO_OPTIONS);
  }, [acceptPosition, handleGeoError]);

  const handleAllow = useCallback(() => requestLocation(), [requestLocation]);

  const handleDeny = useCallback(async () => {
    setShowLocationPrompt(false);
    const uid = auth.currentUser?.uid;
    if (uid) {
      await setDoc(doc(firestore, 'userprofiles', uid), {
        onlineStatus: 'offline',
        location: deleteField(),
        locationUpdatedAt: serverTimestamp(),
      }, { merge: true });
    }
  }, []);

  const recenter = useCallback((map) => {
    if (userCenter && map) {
      map.panTo(userCenter);
      map.setZoom(14);
      return;
    }
    requestLocation();
  }, [requestLocation, userCenter]);

  useEffect(() => {
    let permissionStatus;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        permissionStatus = status;
        setPermissionState(status.state);
        status.onchange = () => setPermissionState(status.state);
      }).catch(() => {});
    }
    return () => { if (permissionStatus) permissionStatus.onchange = null; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        if (!cancelled) setProfileChecked(true);
        return;
      }
      try {
        const snap = await getDoc(doc(firestore, 'userprofiles', uid));
        if (!cancelled && snap.exists()) {
          const data = snap.data();
          if (data.onlineStatus === 'online' && data.location instanceof GeoPoint) {
            setUserCenter({ lat: data.location.latitude, lng: data.location.longitude });
          }
        }
      } finally {
        if (!cancelled) setProfileChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (permissionState !== 'granted' || !navigator.geolocation) return undefined;
    watchRef.current = navigator.geolocation.watchPosition(acceptPosition, handleGeoError, {
      ...GEO_OPTIONS,
      maximumAge: 60000,
    });
    return () => {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    };
  }, [acceptPosition, handleGeoError, permissionState]);

  return {
    profileChecked,
    showLocationPrompt,
    setShowLocationPrompt,
    userCenter,
    accuracy,
    permissionState,
    locationError,
    handleAllow,
    handleDeny,
    requestLocation,
    recenter,
  };
}
